import {
  LeadCapturePage_giftValue,
  LeadCapturePage,
} from '__generated__/graphql';
import { isAfter, isBefore } from 'date-fns';
import { nonNull } from 'utils/array';

export interface Criteria {
  attributeType: string;
  rangeValue: {
    minimum?: number;
    maximum?: number;
  };
  startingRangePreposition?: string;
  endingRangePreposition?: string;
  prefix?: string;
  suffix?: string;
}

export interface Gift {
  criteriaList: Criteria[];
  giftUid: string;
  giftTitle: string;
  mobileImage: string;
  giftValue: LeadCapturePage_giftValue;
}

export interface CatalogueProductOffers {
  exclusiveOffers?: Offer[];
  welcomeOffers?: Offer[];
}

export interface Faq_offer {
  heading: string;
  subHeading: string;
  items: {
    question: string;
    answer: string;
  }[];
}

export interface Offer {
  offerTitle: string;
  description: string;
  startTime: string;
  endTime: string;
  gifts?: Gift[];
  faqs?: Faq_offer[];
}

// Function to get active offers
export const getActiveOffers = (
  activeOffers?: CatalogueProductOffers['exclusiveOffers'],
): {
  offerTitle: string;
  description: string;
  startTime: string;
  endTime: string;
  gifts?: Gift[];
}[] => {
  if (!activeOffers) return [];

  const now = new Date();

  return activeOffers.filter((offer) => {
    const start = offer?.startTime ? new Date(offer.startTime) : null;
    const end = new Date(offer.endTime);
    return (start === null || start <= now) && now <= end;
  });
};

// Function to get reward details by maximum
export const getRewardDetails = (criteriaList: Criteria[]): JSX.Element => {
  const formatRange = (criteria: Criteria) => {
    const rangeValue = criteria.rangeValue;
    const hasMaximum =
      rangeValue.maximum !== undefined && rangeValue.maximum !== null;
    const rangeText = hasMaximum
      ? `${criteria.startingRangePreposition} ${criteria.prefix}${rangeValue.minimum?.toLocaleString()} ${criteria.endingRangePreposition} ${criteria.prefix}${rangeValue.maximum?.toLocaleString()} ${criteria.suffix}`
      : `${criteria.startingRangePreposition} ${criteria.prefix}${rangeValue.minimum?.toLocaleString()} ${criteria.suffix}`;
    return rangeText;
  };

  const loanRange = criteriaList.filter(
    (criteria) => criteria.attributeType === 'ELIGIBLE_LOAN_RANGE',
  );
  const tenureRange = criteriaList.filter(
    (criteria) => criteria.attributeType === 'ELIGIBLE_TENURE_RANGE',
  );

  const loanRangeStr = loanRange.map((criteria, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <span key={index}>{formatRange(criteria)}</span>
  ));

  const tenureRangeStr = tenureRange.map((criteria, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <span key={index}>{formatRange(criteria)}</span>
  ));

  return (
    <>
      {loanRangeStr} + {tenureRangeStr}
    </>
  );
};

// Function to get the largest gift by maximum
export const getLargestGift = (
  gifts?: Gift[],
): { giftTitle: string; maxLoanMin: number } | null => {
  if (!gifts || gifts.length === 0) return null;

  let largestGift: { giftTitle: string; maxLoanMin: number } | null = null;

  for (const gift of gifts ?? []) {
    const loanRange = (gift.criteriaList ?? []).filter(
      (criteria) => criteria.attributeType === 'ELIGIBLE_LOAN_RANGE',
    );

    const maxLoanMin = loanRange.reduce(
      (max, criteria) => Math.max(max, criteria.rangeValue?.minimum ?? 0),
      0,
    );

    if (!largestGift || maxLoanMin > largestGift.maxLoanMin) {
      largestGift = { giftTitle: gift.giftTitle, maxLoanMin };
    }
  }

  return largestGift;
};

// Function to get just the largest gift title
export const getLargestGiftTitle = (gifts?: Gift[]): string => {
  const largestGift = getLargestGift(gifts);
  return largestGift?.giftTitle || '';
};

// Function to get all active offers
export const getAllActiveOffers = (
  product: CatalogueProductOffers,
): Offer[] => {
  const exclusiveOffers = product?.exclusiveOffers;
  const welcomeOffers = product?.welcomeOffers;

  const activeExclusiveOffers = getActiveOffers(exclusiveOffers);
  const activeWelcomeOffers = getActiveOffers(welcomeOffers);

  return [...(activeExclusiveOffers || []), ...(activeWelcomeOffers || [])];
};

// Function to get all gifts
export const getSortedGifts = (gifts?: Gift[]): Gift[] => {
  if (!gifts || gifts.length === 0) return [];

  // Sorting gifts by the largest maxLoanMin value
  return gifts
    .map((gift) => {
      const loanRange = gift.criteriaList.filter(
        (criteria) => criteria.attributeType === 'ELIGIBLE_LOAN_RANGE',
      );

      const maxLoanMin = loanRange.reduce(
        (max, criteria) => Math.max(max, criteria.rangeValue?.minimum ?? 0),
        0,
      );

      return { ...gift, maxLoanMin };
    })
    .sort((a, b) => b.maxLoanMin - a.maxLoanMin); // Sort descending by maxLoanMin
};

// get the highest gift value
export const getHighestGiftValue = (
  offers: Offer[],
): LeadCapturePage_giftValue => {
  const gifts = offers.flatMap((offer) => offer.gifts || []);

  gifts.sort(
    (a, b) => Number(b.giftValue?.price ?? 0) - Number(a.giftValue?.price ?? 0),
  );

  return gifts[0]?.giftValue;
};

export const getOffersBasedOnCriteria = (
  product: CatalogueProductOffers,
  loanAmount?: number,
): Offer[] => {
  const allActiveOffers = getAllActiveOffers(product);
  if (loanAmount === undefined) {
    return allActiveOffers;
  }

  const filterOffers = allActiveOffers
    .map((offer) => {
      const filteredGifts = offer.gifts?.filter((gift) => {
        const loanRange = gift.criteriaList.filter(
          (criteria) => criteria.attributeType === 'ELIGIBLE_LOAN_RANGE',
        );

        return loanRange.some(
          (criteria) =>
            (criteria.rangeValue.minimum ?? 0) <= loanAmount &&
            (criteria.rangeValue.maximum === undefined ||
              criteria.rangeValue.maximum >= loanAmount),
        );
      });

      return { ...offer, gifts: filteredGifts };
    })
    .filter((offer) => offer.gifts && offer.gifts.length > 0);

  return filterOffers;
};

export const getMinimumLoanAmount = (
  leadCapture: LeadCapturePage,
  market: string,
): number => {
  let defaultLoanAmount = market === 'singsaver' ? 30000 : 200001;
  if (leadCapture?.product?.loanAmount) {
    defaultLoanAmount = Number(leadCapture.product.loanAmount);
  }

  const allOffers = leadCapture?.product
    ?.catalogueProduct as CatalogueProductOffers;
  const allActiveOffers = getAllActiveOffers(allOffers);
  if (allActiveOffers.length === 0) {
    return defaultLoanAmount;
  }
  const lowestLoanAmount = allActiveOffers.map((offer) => {
    const minLoanAmounts = offer.gifts?.map((gift) => {
      const loanRange = gift.criteriaList.filter(
        (criteria) => criteria.attributeType === 'ELIGIBLE_LOAN_RANGE',
      );
      let minLoanAmount = 0;
      if (loanRange.length > 0) {
        minLoanAmount = loanRange.reduce(
          (min, criteria) => Math.min(min, criteria.rangeValue?.minimum ?? 0),
          Infinity,
        );
      }
      return minLoanAmount;
    });
    return Math.min(...(minLoanAmounts ?? []));
  });
  const minimumLoanAmount = Math.min(...lowestLoanAmount);
  return minimumLoanAmount < defaultLoanAmount
    ? defaultLoanAmount
    : minimumLoanAmount;
};

export const getMinimumLoanTenure = (
  loanTime?: string,
  loanTimeUnit?: string,
): number => {
  let loanTimeInMonth = 36;
  if (loanTime && loanTimeUnit) {
    const parsedLoanTime = Number(loanTime);

    if (!Number.isNaN(parsedLoanTime)) {
      loanTimeInMonth =
        loanTimeUnit === 'month' ? parsedLoanTime : parsedLoanTime * 12;
    }
  }

  return loanTimeInMonth;
};

export const getFaqOffer = (exclusiveOffers: Offer[]): Faq_offer => {
  const now = new Date();
  return exclusiveOffers
    ?.filter((offer) => {
      if (offer?.startTime && isBefore(now, offer.startTime)) return false;
      if (offer?.endTime && isAfter(now, offer.endTime)) return false;
      return true;
    })
    .flatMap((offer) => offer?.faqs || [])
    .filter(nonNull)[0];
};
